import { Fragment, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useGetList, useGetMany, useTranslate } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Contact } from "../types";
import { formatUsd, type Shipment } from "../shipments/types";

// Per-channel funnel for the performance page: inquiries (contacts created in the period),
// buyers and sales (non-voided shipments invoiced in the period, attributed through the
// shipment's contact). Non-admins only see their own shipments (RLS), so their sales columns
// cover their own deals; inquiries count every contact since contacts are shared.

export const NONE = "\u0000none";
export const NO_CONTACT = "\u0000no-contact";

type Totals = {
  inquiries: number;
  buyers: Set<unknown>;
  external: number;
  intercompany: number;
};
const emptyTotals = (): Totals => ({
  inquiries: 0,
  buyers: new Set(),
  external: 0,
  intercompany: 0,
});

const key = (v?: string | null) => v?.trim() || NONE;

// channel -> source detail -> totals. Shipments without a contact land under "not recorded".
export const aggregateChannels = (
  newContacts: Contact[],
  shipments: Shipment[],
  buyerContacts: Contact[],
): Map<string, Map<string, Totals>> => {
  const channels = new Map<string, Map<string, Totals>>();
  const bucket = (channel: string, detail: string) => {
    if (!channels.has(channel)) channels.set(channel, new Map());
    const details = channels.get(channel)!;
    if (!details.has(detail)) details.set(detail, emptyTotals());
    return details.get(detail)!;
  };
  for (const c of newContacts) {
    bucket(key(c.lead_source), key(c.lead_source_detail)).inquiries += 1;
  }
  const byId = new Map(buyerContacts.map((c) => [String(c.id), c]));
  for (const s of shipments) {
    const c = s.contact_id != null ? byId.get(String(s.contact_id)) : undefined;
    const t =
      s.contact_id == null
        ? bucket(NONE, NO_CONTACT)
        : bucket(key(c?.lead_source), key(c?.lead_source_detail));
    if (s.contact_id != null) t.buyers.add(String(s.contact_id));
    if (s.trade_type === "intercompany") t.intercompany += Number(s.total);
    else t.external += Number(s.total);
  }
  return channels;
};

export const ChannelStats = ({
  fromIso,
  toIso,
  shipments,
}: {
  // [fromIso, toIso) in the browser's time zone, matching the month range picker
  fromIso: string;
  toIso: string;
  shipments: Shipment[];
}) => {
  const translate = useTranslate();
  const { leadSources } = useConfigurationContext();
  const [open, setOpen] = useState<Set<string>>(new Set());

  // ponytail: one request for the period's new contacts, one for the buyers' contacts.
  const { data: newContacts = [], isPending: newPending } = useGetList<Contact>(
    "contacts",
    {
      pagination: { page: 1, perPage: 10000 },
      sort: { field: "id", order: "ASC" },
      filter: { "first_seen@gte": fromIso, "first_seen@lt": toIso },
    },
  );
  const buyerIds = [
    ...new Set(shipments.map((s) => s.contact_id).filter((id) => id != null)),
  ];
  const { data: buyerContacts = [], isPending: buyersPending } =
    useGetMany<Contact>(
      "contacts",
      { ids: buyerIds },
      { enabled: buyerIds.length > 0 },
    );

  const channels = aggregateChannels(newContacts, shipments, buyerContacts);

  const sum = (rows: Iterable<Totals>) => {
    const out = emptyTotals();
    for (const r of rows) {
      out.inquiries += r.inquiries;
      r.buyers.forEach((b) => out.buyers.add(b));
      out.external += r.external;
      out.intercompany += r.intercompany;
    }
    return out;
  };

  // Configured channels first (in Settings order, even when empty), then values removed
  // from Settings but still stored, then "not recorded".
  const configured = leadSources.map((s) => s.value);
  const order = [
    ...configured,
    ...[...channels.keys()].filter(
      (k) => k !== NONE && !configured.includes(k),
    ),
    NONE,
  ];
  const channelLabel = (k: string) =>
    k === NONE
      ? translate("egi.lead_source.unknown")
      : (leadSources.find((s) => s.value === k)?.label ?? k);
  const detailLabel = (k: string) =>
    k === NONE
      ? translate("egi.channels.no_detail")
      : k === NO_CONTACT
        ? translate("egi.channels.no_contact")
        : k;

  const grand = sum([...channels.values()].flatMap((d) => [...d.values()]));
  const toggle = (k: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  const Cells = ({ t }: { t: Totals }) => (
    <>
      <td className="py-2 pr-4 text-right tabular-nums">{t.inquiries}</td>
      <td className="py-2 pr-4 text-right tabular-nums">{t.buyers.size}</td>
      <td className="py-2 pr-4 text-right tabular-nums">
        {formatUsd(t.external)}
      </td>
      <td className="py-2 text-right tabular-nums">
        {formatUsd(t.intercompany)}
      </td>
    </>
  );

  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <div>
          <h2 className="text-lg font-semibold">
            {translate("egi.channels.title")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {translate("egi.channels.note")}
          </p>
        </div>
        {newPending || (buyerIds.length > 0 && buyersPending) ? (
          <div className="text-sm text-muted-foreground">
            {translate("egi.performance.loading")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-foreground text-left">
                  <th className="py-2 pr-4 font-semibold">
                    {translate("egi.channels.channel")}
                  </th>
                  <th className="py-2 pr-4 font-semibold text-right whitespace-nowrap">
                    {translate("egi.channels.inquiries")}
                  </th>
                  <th className="py-2 pr-4 font-semibold text-right whitespace-nowrap">
                    {translate("egi.channels.buyers")}
                  </th>
                  <th className="py-2 pr-4 font-semibold text-right whitespace-nowrap">
                    {translate("egi.channels.external")}
                  </th>
                  <th className="py-2 font-semibold text-right whitespace-nowrap">
                    {translate("egi.channels.intercompany")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {order.map((ch) => {
                  const details = channels.get(ch);
                  const t = sum(details?.values() ?? []);
                  const empty =
                    t.inquiries === 0 &&
                    t.buyers.size === 0 &&
                    t.external === 0 &&
                    t.intercompany === 0;
                  const isOpen = open.has(ch);
                  const detailKeys = [...(details?.keys() ?? [])].sort(
                    (a, b) =>
                      (a === NONE || a === NO_CONTACT ? 1 : 0) -
                        (b === NONE || b === NO_CONTACT ? 1 : 0) ||
                      a.localeCompare(b),
                  );
                  return (
                    <Fragment key={ch}>
                      <tr
                        className={`border-b ${empty ? "text-muted-foreground" : ""}`}
                      >
                        <td className="py-2 pr-4">
                          {detailKeys.length > 0 ? (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 text-left hover:underline"
                              aria-expanded={isOpen}
                              aria-label={translate("egi.channels.toggle")}
                              onClick={() => toggle(ch)}
                            >
                              {isOpen ? (
                                <ChevronDown className="size-4" />
                              ) : (
                                <ChevronRight className="size-4" />
                              )}
                              {channelLabel(ch)}
                            </button>
                          ) : (
                            <span className="pl-5">{channelLabel(ch)}</span>
                          )}
                        </td>
                        <Cells t={t} />
                      </tr>
                      {isOpen
                        ? detailKeys.map((d) => (
                            <tr
                              key={d}
                              className="border-b bg-muted/40 text-muted-foreground"
                            >
                              <td className="py-1.5 pr-4 pl-8 break-words max-w-64">
                                {detailLabel(d)}
                              </td>
                              <Cells t={details!.get(d)!} />
                            </tr>
                          ))
                        : null}
                    </Fragment>
                  );
                })}
                <tr className="font-semibold">
                  <td className="py-2 pr-4 pl-5">
                    {translate("egi.performance.all")}
                  </td>
                  <Cells t={grand} />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
